'use client';

import { c as _c } from "react/compiler-runtime";
import { useSearchParams, usePathname } from 'next/navigation';
import Button from '@classify/react-core-components/Button';
import { useTheme, styled } from '@classify/react-core-components/styles';
import Link from 'next/link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { translate } from '@openclassrooms/translations';
import Typography from '@classify/react-core-components/Typography';
import { TocVariants } from './TableOfContent/TableOfContent';
const Root = styled('div')(({
  theme: {
    spacing,
    palette,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: palette.background.default,
  padding: spacing(2),
  margin: spacing(4, 0),
  border: `1px solid ${palette.stroke}`,
  [breakpoints.up('sm')]: {
    flexDirection: 'row'
  }
}));
const StatusWrapper = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 'auto',
  marginBottom: spacing(3),
  [breakpoints.up('sm')]: {
    flexDirection: 'row',
    marginRight: '0',
    marginBottom: '0'
  }
}));
const StatusLabel = styled(Typography)(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'left',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline'
  },
  '&:not(:last-child)': {
    marginBottom: spacing(2)
  },
  [breakpoints.up('sm')]: {
    textAlign: 'center',
    '&:not(:last-child)': {
      marginBottom: '0'
    },
    '&:not(:first-of-type)': {
      marginLeft: spacing(3)
    }
  }
}));
const StatusIcon = styled(VisibilityIcon)(({
  theme: {
    spacing
  }
}) => ({
  marginRight: spacing(1),
  width: 18
}));
export interface EditionPanelProps {
  courseId?: string;
  chapterId?: string;
  exerciseId?: string;
  isPublished?: boolean;
  variant?: keyof typeof TocVariants;
}
const EditionPanel = t0 => {
  const $ = _c(30);
  const {
    courseId,
    chapterId,
    exerciseId,
    isPublished,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? TocVariants.HOMEPAGE : t1;
  const {
    palette
  } = useTheme();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = translate("course.chapter.admin_section.actions.edit_course");
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `/courses/${courseId}/edit`;
  let t4;
  if ($[1] !== t3) {
    t4 = {
      label: t2,
      href: t3,
      hasStatus: true
    };
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = translate("course.chapter.admin_section.actions.edit");
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = `/courses/${courseId}/parts/${chapterId}/edit`;
  let t7;
  if ($[4] !== t6) {
    t7 = {
      label: t5,
      href: t6,
      hasStatus: true
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = translate("course.quiz.admin_section.actions.edit");
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = `/exercises/${exerciseId}/edit`;
  let t10;
  if ($[7] !== t9) {
    t10 = {
      label: t8,
      href: t9,
      hasStatus: false
    };
    $[7] = t9;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== t10 || $[10] !== t4 || $[11] !== t7) {
    t11 = {
      [TocVariants.HOMEPAGE]: t4,
      [TocVariants.CHAPTER]: t7,
      [TocVariants.QUIZ]: t10
    };
    $[9] = t10;
    $[10] = t4;
    $[11] = t7;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const buttonInformation = t11;
  let t12;
  if ($[13] !== isPublished || $[14] !== searchParams) {
    t12 = searchParams?.get("status") === "draft" || !isPublished;
    $[13] = isPublished;
    $[14] = searchParams;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const isDraftStatus = t12;
  let t13;
  if ($[16] !== buttonInformation || $[17] !== isDraftStatus || $[18] !== palette || $[19] !== pathname || $[20] !== variant) {
    t13 = buttonInformation[variant].hasStatus && <StatusWrapper><StatusLabel style={{
        color: isDraftStatus ? palette.brand.main : palette.text.primary
      }} variant="subheadingSmall" data-testid="unpublishedText" component={Link} href={pathname + "?status=draft"}><StatusIcon />{translate("course.chapter.admin_section.status.writing_progress")}</StatusLabel><StatusLabel style={{
        color: !isDraftStatus ? palette.brand.main : palette.text.primary
      }} variant="subheadingSmall" data-testid="publishedText" component={Link} href={pathname}><StatusIcon />{translate("course.chapter.admin_section.status.published")}</StatusLabel></StatusWrapper>;
    $[16] = buttonInformation;
    $[17] = isDraftStatus;
    $[18] = palette;
    $[19] = pathname;
    $[20] = variant;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      marginRight: {
        xs: "auto",
        sm: 0
      },
      marginLeft: {
        xs: 0,
        sm: "auto"
      }
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const t15 = buttonInformation[variant];
  const t16 = buttonInformation[variant];
  const t17 = buttonInformation[variant];
  let t18;
  if ($[23] !== t15.href || $[24] !== t16.label || $[25] !== t17.label) {
    t18 = <Button sx={t14} component="a" href={t15.href} variant="primary" aria-label={t16.label}>{t17.label}</Button>;
    $[23] = t15.href;
    $[24] = t16.label;
    $[25] = t17.label;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== t13 || $[28] !== t18) {
    t19 = <Root data-testid="courseEditionPanel">{t13}{t18}</Root>;
    $[27] = t13;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  return t19;
};
export default EditionPanel;